import { useCallback, useMemo, useRef, useState } from 'react';

import { ARGYA_DND_MIME } from './useDraggable';

/**
 * useChartDropTarget — public SDK hook that makes any element a drop
 * target for widget-library drag payloads (Phase 3).
 *
 * Pure plumbing: parses `dataTransfer`, filters by `accepts`, calls the
 * caller-provided `onDrop` with `(payload, anatomicalRef)`. The hook
 * does NOT decide what happens on drop — chart plugins own that logic
 * (dental looks up the diagnosis on the dropped-on tooth, ortho looks
 * up vertebra findings, etc.). This keeps widget core anatomy-agnostic.
 *
 * Usage (dental tooth element)
 * ----------------------------
 *   const handleDrop = useCallback((payload, ref) => {
 *     if (payload.type !== 'procedure') return;
 *     const tokens = ref.payload?.tokens || [];
 *     const diagnosis = findLatestDiagnosisOnTokens(notesByToken, tokens);
 *     sdk.openWidget('procedure', {
 *       visitId, anatomicalRef: ref,
 *       prefill: { ...payload, tokens, diagnosisMetaId: diagnosis?.meta_id, ... },
 *     }, { placement: 'chart-pane' });
 *   }, [sdk, visit, notesByToken]);
 *
 *   const dropProps = useChartDropTarget({
 *     accepts: ['procedure'],
 *     anatomicalRef: { kind: 'dental-tooth-surface', payload: { tokens: ['16'] }, summary: 'Tooth #16' },
 *     onDrop: handleDrop,
 *   });
 *   return <g {...dropProps}>…tooth SVG…</g>;
 *
 * Visual feedback
 * ---------------
 * While a valid payload is hovered, the hook sets
 * `data-drop-active="true"` on the element. Consumers add a CSS rule
 * (e.g. `[data-drop-active="true"] { outline: 2px dashed teal }`) to
 * style the highlight. Removed automatically on dragleave or drop.
 */

const NO_OP = () => {};

export default function useChartDropTarget({
  accepts,
  anatomicalRef,
  onDrop,
  disabled = false,
} = {}) {
  const [isOver, setIsOver] = useState(false);
  // Dragenter/leave fire on child elements too; track depth so the
  // highlight doesn't flicker when the cursor crosses interior boundaries.
  const depthRef = useRef(0);

  const acceptsList = useMemo(
    () => (Array.isArray(accepts) ? accepts : []),
    [accepts],
  );
  const accepted = useCallback((type) => acceptsList.includes(type), [acceptsList]);

  const readPayload = useCallback((e) => {
    if (!e.dataTransfer) return null;
    const raw = e.dataTransfer.getData(ARGYA_DND_MIME);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      if (!accepted(parsed.type)) return null;
      return parsed;
    } catch (_e) { return null; }
  }, [accepted]);

  const onDragEnter = useCallback((e) => {
    if (disabled) return;
    // During dragenter, `getData` is restricted in most browsers (only
    // the MIME types list is exposed). We use `types` to filter.
    const types = e.dataTransfer?.types;
    if (!types || ![...types].includes(ARGYA_DND_MIME)) return;
    depthRef.current += 1;
    if (depthRef.current === 1) {
      setIsOver(true);
    }
  }, [disabled]);

  const onDragOver = useCallback((e) => {
    if (disabled) return;
    const types = e.dataTransfer?.types;
    if (!types || ![...types].includes(ARGYA_DND_MIME)) return;
    // preventDefault is what tells the browser "I accept this drop".
    // Without it the drop event will not fire.
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
  }, [disabled]);

  const onDragLeave = useCallback(() => {
    if (disabled) return;
    depthRef.current = Math.max(0, depthRef.current - 1);
    if (depthRef.current === 0) setIsOver(false);
  }, [disabled]);

  const onDropHandler = useCallback((e) => {
    if (disabled) return;
    depthRef.current = 0;
    setIsOver(false);
    const payload = readPayload(e);
    if (!payload) return;
    e.preventDefault();
    e.stopPropagation();
    try { (onDrop || NO_OP)(payload, anatomicalRef); }
    catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('[widgets] useChartDropTarget onDrop threw', err);
      }
    }
  }, [disabled, readPayload, onDrop, anatomicalRef]);

  return {
    onDragEnter,
    onDragOver,
    onDragLeave,
    onDrop: onDropHandler,
    'data-drop-active': isOver ? 'true' : undefined,
    'aria-dropeffect': accepts && accepts.length > 0 ? 'copy' : undefined,
    // Phase 7 — expose `isOver` so consumers can render inline hint
    // chips on hover (e.g. "Links to: Tooth #16 · Caries" on diagnosis
    // rows). Consumers that don't need it can keep spreading the
    // entire return object; React will warn about the unknown DOM
    // attribute but it stays a runtime no-op. Consumers that DO want
    // to use it should destructure: `const { isOver, ...dropProps } = …`.
    isOver,
  };
}
